import * as React from 'react';
import styled from 'styled-components';
import strings from '../../strings/strings.json';
import { breakpoints, sizes, theme } from 'theme';
import { Step, StepsPanel } from './components/StepsPanel';
import PageInfoIcon from 'assets/icons/controls/PageInfoIcon';
import FileMultipleIcon from 'assets/icons/FileMultipleIcon';
import FlagIcon from 'assets/icons/FlagIcon';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import useWindowWidth from 'hooks/useWindowWidth';
import { CourseBuilderButtons } from './components/CourseBuilderButtons';
import { CourseDetailsForm } from './components/stepComponents/CourseDetailsForm';
import CourseStepContent from './components/CourseStepContent';
import CourseLearningObjectivesForm from './components/stepComponents/CourseLearningObjectivesForm';
import CourseStructureAndContentComponent from './components/stepComponents/CourseStructureAndContentComponent';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import { CourseGeneratedContentIsApproved, CourseIsReadyToPublish } from 'models/Course';
import BoltIcon from 'assets/icons/BoltIcon';
import CourseGenerateContentComponent from './components/stepComponents/CourseGenerateContentComponent';
import Modal from 'components/modals/Modal';
import PublishCourseWarning from 'pages/trainer/course/PublishCourseWarning';
import useModifyRequest from 'hooks/useModifyRequest';
import { useNavigate } from 'react-router-dom';
import ReviewChanges from './components/ReviewChanges';
import { FlexCol, FlexColMd, FlexLg } from 'components/Elements';
import { courseBuilderBreakpoint } from './shared-styles/courseBuilderFormComponents';
import { ModuleType } from 'models/Module';
import {
    EditActivityFields,
    EditResourceFields,
    EditQuestionFields,
    EditDocumentFields,
    EditModuleFields,
    EditLiveCourseFields,
    EditTaskContentFields,
} from './SharedFields';

export default function CourseBuilderPage(): JSX.Element {
    const navigate = useNavigate();
    const { currentWindowWidth } = useWindowWidth();

    const course = React.useContext(BuildCourseContext).course!;
    const {
        setActiveStep,
        activeStep,
        showIncompleteStepsWarning,
        errors,
        generateContentStatusesState,
        editActivityId,
        editMode,
    } = React.useContext(BuildCourseContext);

    const [stateSteps, setStateSteps] = React.useState<Step[]>([]);
    const [activeStepContent, setActiveStepContent] = React.useState<JSX.Element>();
    const [showPublishWarning, setShowPublishWarning] = React.useState<boolean>(false);
    const [showPublishModal, setShowPublishModal] = React.useState<boolean>(false);
    const [showReviewModal, setShowReviewModal] = React.useState<boolean>(false);
    const [editLiveCourseError, setEditLiveCourseError] = React.useState<boolean>(false);

    const { modifyData: updateCourse, loading: loadingUpdateCourse } = useModifyRequest<EditLiveCourseFields, boolean>(
        `courses/${course.id}/edit-live-course`,
        'PATCH',
    );

    React.useEffect(() => {
        if (course) {
            const steps: Step[] = [
                {
                    id: 'courseDetails',
                    title: strings.courseEditorPage.steps.courseDetails.title,
                    aria: strings.courseEditorPage.steps.courseDetails.aria,
                    icon: PageInfoIcon,
                    action: () => setActiveStep('courseDetails'),
                    content: (
                        <StepContent id="step-content" $maxwidth={'750px'}>
                            <CourseStepContent
                                key={'courseDetails'}
                                title={strings.courseEditorPage.steps.courseDetails.title}
                                hint={editMode ? '' : strings.courseEditorPage.steps.courseDetails.hint}
                                content={<CourseDetailsForm />}
                            />
                        </StepContent>
                    ),
                    completed: !!course.title && !!course.description,
                },
                {
                    id: 'courseLearningObjectives',
                    title: strings.courseEditorPage.steps.learningObjectives.title,
                    aria: strings.courseEditorPage.steps.learningObjectives.aria,
                    icon: FlagIcon,
                    action: () => setActiveStep('courseLearningObjectives'),
                    content: (
                        <StepContent id="step-content" $maxwidth={'750px'}>
                            <CourseStepContent
                                key={'courseLearningObjectives'}
                                title={strings.courseEditorPage.steps.learningObjectives.title}
                                hint={editMode ? '' : strings.courseEditorPage.steps.learningObjectives.hint}
                                content={<CourseLearningObjectivesForm />}
                            />
                        </StepContent>
                    ),
                    completed: course.learningObjectives.length >= 1,
                },
                {
                    id: 'courseStructureAndContent',
                    title: strings.courseEditorPage.steps.structureAndContent.title,
                    aria: strings.courseEditorPage.steps.structureAndContent.aria,
                    icon: FileMultipleIcon,
                    action: () => setActiveStep('courseStructureAndContent'),
                    content: (
                        <StepContent id="step-content" $maxwidth={'1400px'}>
                            <CourseStepContent
                                key={'courseStructureAndContent'}
                                content={<CourseStructureAndContentComponent />}
                            />
                        </StepContent>
                    ),
                    completed: course.modules.length >= 1 && course.modules.every((m) => m.activities.length >= 1),
                },
            ];

            if (!editMode) {
                steps.push({
                    id: 'courseAIGeneratedContent',
                    title: strings.courseEditorPage.steps.aiGeneratedContent.title,
                    aria: strings.courseEditorPage.steps.aiGeneratedContent.aria,
                    icon: BoltIcon,
                    action: () => setActiveStep('courseAIGeneratedContent'),
                    content: (
                        <StepContent id="step-content" $maxwidth={'1400px'}>
                            <CourseStepContent
                                key={'courseAIGeneratedContent'}
                                content={<CourseGenerateContentComponent />}
                            />
                        </StepContent>
                    ),
                    completed:
                        course.modules.length > 0 &&
                        (course.modules.every((m) => m.moduleType === ModuleType.PRESENTATION) ||
                            CourseGeneratedContentIsApproved(generateContentStatusesState)),
                });
            }

            setStateSteps(steps);

            if (CourseIsReadyToPublish(course, generateContentStatusesState)) {
                setShowPublishWarning(false);
            }
        }
    }, [course, showIncompleteStepsWarning, showPublishWarning, editActivityId, generateContentStatusesState]); // eslint-disable-line

    React.useEffect(() => {
        const step = stateSteps.find((step) => step.id === activeStep);
        if (step) {
            setActiveStepContent(step.content);
        }
    }, [activeStep, stateSteps]);

    React.useEffect(() => {
        if (activeStepContent) {
            window.scrollTo({
                top: 0,
                behavior: 'auto',
            });
        }
    }, [activeStepContent]);

    if (errors.length > 0 || !course) {
        return (
            <PageContainer id="course-editor-content">
                <ErrorNotifier title={'Error'} description={'No course found.'} />
            </PageContainer>
        );
    }

    const courseReadyToPublish = CourseIsReadyToPublish(course, generateContentStatusesState);

    const handleConfirmReview = async () => {
        setEditLiveCourseError(false);

        const editModuleFields: EditModuleFields[] = course.modules.map((m) => ({
            id: m.id,
            title: m.title,
            description: m.description,
            type: m.moduleType,
            activities: m.activities.map((a): EditActivityFields => {
                const resources: EditResourceFields[] = a.resources.map((r) => ({
                    title: r.title,
                    url: r.url,
                }));
                const questions: EditQuestionFields[] = a.questions.map((q) => ({
                    id: q.id,
                    question: q.question,
                    hint: q.hint,
                }));
                const taskContents: EditTaskContentFields[] = a.taskContents.map((tc) => {
                    switch (tc.type) {
                        default:
                        case 'image':
                            if (tc.id === '') {
                                const fields: EditTaskContentFields = {
                                    type: 'addImage',
                                    fileName: tc.image.filename,
                                    filePath: tc.image.s3Filepath,
                                    imageDescription: tc.imageDescription,
                                };
                                return fields;
                            } else {
                                const fields: EditTaskContentFields = {
                                    type: 'editImage',
                                    id: tc.id,
                                    imageDescription: tc.imageDescription,
                                };
                                return fields;
                            }
                        case 'text':
                            if (tc.id === '') {
                                const fields: EditTaskContentFields = {
                                    type: 'addText',
                                    text: tc.text,
                                };
                                return fields;
                            } else {
                                const fields: EditTaskContentFields = {
                                    type: 'editText',
                                    id: tc.id,
                                    text: tc.text,
                                };
                                return fields;
                            }
                    }
                });
                const imageDocuments: EditDocumentFields[] = a.imageDocuments.map((doc) =>
                    doc.id
                        ? { type: 'existing', id: doc.id }
                        : { type: 'new', fileName: doc.filename, filePath: doc.s3Filepath },
                );
                const mapDocument = (doc?: typeof a.audioDocument): EditDocumentFields => {
                    if (!doc) return null;
                    if (doc.id) return { type: 'existing', id: doc.id };
                    return { type: 'new', fileName: doc.filename!, filePath: doc.s3Filepath! };
                };

                const editActivityFields: EditActivityFields = {
                    id: a.id,
                    title: a.title,
                    description: a.description,
                    duration: a.durationInMins ?? 0,
                    type: a.type,
                    buildMethod: a.buildMethod,
                    content: a.content,
                    taskContents: taskContents,
                    resources: resources,
                    questions: questions,
                    imageDocuments: imageDocuments,
                    audioDocument: mapDocument(a.audioDocument),
                    videoDocument: mapDocument(a.videoDocument),
                };

                return editActivityFields;
            }),
        }));

        const editLiveCourseFields: EditLiveCourseFields = {
            courseTitle: course.title,
            courseDescription: course.description,
            learningObjectives: course.learningObjectives.map((lo) => lo.description),
            modules: editModuleFields,
        };

        const { errors } = await updateCourse(editLiveCourseFields);
        if (errors && errors.length > 0) {
            setEditLiveCourseError(true);
            return;
        }
        setShowReviewModal(false);
        navigate(`/published-course/${course.id}`);
    };

    return (
        <PageContainer id="course-editor-content">
            <PageContentContainer id="course-editor-content-grid">
                {currentWindowWidth <= courseBuilderBreakpoint ? (
                    <FlexColMd>
                        <StepsPanel courseId={course.id} steps={stateSteps} activeStep={activeStep} />
                        <CourseBuilderButtons
                            courseId={course.id}
                            courseReadyToPublish={courseReadyToPublish}
                            showPublishWarning={showPublishWarning}
                            setShowPublishWarning={setShowPublishWarning}
                            setShowPublishModal={setShowPublishModal}
                            setShowReviewModal={setShowReviewModal}
                        />
                    </FlexColMd>
                ) : (
                    <>
                        <StepsAndButtonsPanel id="steps-panel">
                            <StepsPanel courseId={course.id} steps={stateSteps} activeStep={activeStep} />
                            <CourseBuilderButtons
                                courseId={course.id}
                                courseReadyToPublish={courseReadyToPublish}
                                showPublishWarning={showPublishWarning}
                                setShowPublishWarning={setShowPublishWarning}
                                setShowPublishModal={setShowPublishModal}
                                setShowReviewModal={setShowReviewModal}
                            />
                        </StepsAndButtonsPanel>
                        {activeStepContent}
                    </>
                )}
            </PageContentContainer>

            {showPublishModal && (
                <Modal
                    headlineText={strings.publishCourseWarningPage.publishWarning.headlineText}
                    modalBody={<PublishCourseWarning setModalOpen={setShowPublishModal} />}
                    onClickCloseButton={() => setShowPublishModal(false)}
                />
            )}

            {showReviewModal && (
                <Modal
                    headlineText={strings.courseEditorPage.reviewChangesModalHeadline}
                    onClickCloseButton={() => setShowReviewModal(false)}
                    big
                    modalBody={
                        <ReviewChanges
                            closeModal={() => setShowReviewModal(false)}
                            onConfirm={handleConfirmReview}
                            saving={loadingUpdateCourse}
                            editLiveCourseError={editLiveCourseError}
                        />
                    }
                />
            )}
        </PageContainer>
    );
}

const StepsAndButtonsPanel = styled(FlexCol)`
    justify-content: space-between;
    border-right: 1px solid ${theme.cardSecondaryBorder};
    min-width: 340px;
    max-width: 340px;
    height: calc(100vh - 100px);
    position: sticky;
    top: 80px;
    overflow-y: auto; //for short device viewports
`;

const StepContent = styled(FlexCol)<{ $maxwidth: string }>`
    padding: ${sizes.spacingLg} 0;
    max-width: ${({ $maxwidth }) => $maxwidth};
`;

const PageContentContainer = styled(FlexLg)`
    @media (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        margin-top: ${sizes.spacingMd};
    }
`;

const PageContainer = styled(FlexCol)`
    padding: 0 ${sizes.edgePadding};
`;
